import { VideoType } from '../videoPreview/constants';

/**
 * Who a video notification is delivered to — mirrors the API's `targetType`.
 * Note the API spells the agency bucket singular while carrying a csv list of
 * ids in `agencyIds`, so one option covers "one or many agencies".
 */
export enum VideoNotificationAudienceType {
  ALL_AGENTS = 'all_agents',
  AGENCY = 'agency',
  AGENTS = 'agents',
}

/** `videoType` values accepted / returned by the API. */
export type VideoNotificationVideoType = VideoType.URL | VideoType.MP4;

/**
 * Video notification as the API returns it.
 *
 *   Operator CRUD  → /ms/admin/video-notifications
 *   Agent queue    → /ms/video-notifications/active
 *
 * Fields are optional where the API may send null, so a partially populated
 * record never breaks the UI.
 */
export interface VideoNotificationDto {
  id: number;
  tenantId?: number;
  title?: string;
  description?: string | null;
  videoType?: VideoNotificationVideoType | string;
  /** the uploaded mp4 URL, or the youtube / drive link when videoType is 'url' */
  videoPath?: string | null;
  mustWatch?: boolean;
  targetType?: VideoNotificationAudienceType | string;
  /** csv string ("1,2") or array, depending on the endpoint */
  agencyIds?: string | Array<number | string> | null;
  /** user ids — csv string or array */
  agentIds?: string | Array<number | string> | null;
  isActive?: boolean;
  userCreated?: number;
  /** ISO timestamp */
  dateCreated?: string | number | null;
  dateUpdated?: string | number | null;
  deleted?: string | null;
  /**
   * Not sent by the API — the /active queue only returns what is still
   * pending. The client sets this once "Done" has been posted, so the panel can
   * keep showing the item in its watched state for the rest of the session.
   */
  isWatched?: boolean;
}

/** Normalized shape the UI components work with. */
export interface MustWatchVideoNotification {
  id: number;
  title: string;
  description: string;
  videoType: VideoNotificationVideoType;
  /** direct <video> source, when videoType is mp4 */
  videoPath?: string;
  /** <iframe> source resolved from the youtube / drive link */
  embedSrc?: string;
  /** poster image derived from the youtube / drive id, when available */
  thumbnail?: string;
  /** epoch milliseconds, ready for moment() */
  dateCreatedMs?: number;
  targetType: VideoNotificationAudienceType;
  agencyIds: number[];
  agentIds: number[];
  isActive: boolean;
  isWatched: boolean;
  /** false only when the author explicitly turned the blocking behaviour off */
  isMustWatch: boolean;
  /** the untouched object, so the "Done" handler can post it back as-is */
  raw: VideoNotificationDto;
}

export interface MustWatchVideoTranslations {
  /** modal fallback heading, e.g. "New feature — required viewing" */
  title: string;
  /** badge on must-watch items, e.g. "Must watch" */
  badge: string;
  /** badge on non-blocking items, e.g. "Optional" */
  optionalLabel: string;
  /** tooltip / aria-label of the bell */
  indicatorTooltip: string;
  /** hint rendered on the blocking overlay */
  blockedHint: string;
  /** panel heading */
  notificationsTitle: string;
  /** chip on unseen items */
  newLabel: string;
  /** chip on already-watched items */
  watchedLabel: string;
  seeMore: string;
  seeLess: string;
  emptyState: string;
  /** modal navigation between unseen videos of the same kind */
  prevVideo: string;
  nextVideo: string;
  /** joiner for the "2 of 3" counter */
  counterOf: string;
  /** confirm button label */
  done: string;
  /** dismiss button label for non-blocking videos */
  closeLabel: string;
  /** fallback text when the video cannot be rendered */
  videoUnavailable: string;
}

/**
 * Class name of the bell button. The interaction shield cuts a hole around
 * this element, so it is the entry point to the notification panel while the
 * must-watch lock is on.
 */
export const VIDEO_NOTIFICATION_BELL_CLASS = 'video-notification-bell';

/** Panel overlay class — kept interactive while the shield is up. */
export const VIDEO_NOTIFICATION_PANEL_CLASS = 'video-notification-popover';

/** How many notifications the panel shows before "see more". */
export const VIDEO_NOTIFICATION_PAGE_SIZE = 7;

/** react-query key for the agent-facing pending queue. */
export const ACTIVE_VIDEO_NOTIFICATIONS_QUERY_KEY = 'video-notifications-active';
